import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANG_LABEL } from "@/lib/pgt";
import { Loader2, Plus, Copy, FileText, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const { user } = useAuth();
  const { meta } = useMeta();
  const qc = useQueryClient();
  const isManager = user.role === "admin" || user.role === "manager";
  const [dialog, setDialog] = useState(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get("/templates")).data,
  });

  const copy = (body) => { navigator.clipboard.writeText(body); toast.success("Template copied"); };

  return (
    <div className="animate-fade-up">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Message templates</h1>
          <p className="mt-1 text-sm text-stone-500">Approved starting points. The copilot personalizes these per customer.</p>
        </div>
        {isManager && <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => setDialog({ mode: "add" })} data-testid="add-template-button"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-16 text-center">
          <FileText className="mx-auto h-10 w-10 text-stone-300" />
          <p className="mt-3 text-sm text-stone-500">No templates yet.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-semibold text-stone-900">{t.title}</div>
                  <div className="text-[11px] text-stone-400">{t.template_type}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline" className="text-[10px]">{LANG_LABEL[t.language] || t.language}</Badge>
                  {!t.active && <span className="text-[10px] text-stone-400">inactive</span>}
                </div>
              </div>
              <p className="mt-2 rounded-lg bg-whatsapp-bg/30 p-2.5 text-sm text-stone-700 whitespace-pre-wrap line-clamp-5">{t.body}</p>
              <div className="mt-3 flex gap-1.5">
                <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => copy(t.body)} data-testid={`template-copy-${t.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                {isManager && <Button size="sm" variant="ghost" className="text-xs" onClick={() => setDialog({ mode: "edit", template: t })} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {dialog && <TemplateDialog dialog={dialog} onClose={() => setDialog(null)} meta={meta} onSaved={() => { qc.invalidateQueries({ queryKey: ["templates"] }); setDialog(null); }} />}
    </div>
  );
}

function TemplateDialog({ dialog, onClose, meta, onSaved }) {
  const editing = dialog.mode === "edit";
  const t = dialog.template || {};
  const [form, setForm] = useState({
    template_type: t.template_type || (meta?.template_types?.[0] || "First introduction"),
    title: t.title || "", language: t.language || "hinglish", body: t.body || "",
    stage: t.stage || "stage_1_intro", active: t.active !== false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setLoading(true);
    try {
      if (editing) await api.patch(`/templates/${t.id}`, form);
      else await api.post("/templates", form);
      toast.success(editing ? "Template updated" : "Template added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit template" : "Add template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="template-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">Type</Label>
              <Select value={form.template_type} onValueChange={(v) => set("template_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{(meta?.template_types || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">Language</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.languages || []).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">Body (use {"{name}"} for customer name)</Label><Textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={5} data-testid="template-body-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="template-save">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
